import { AssetType, TransformationTemplate } from '@amzn/fire-tv-campaign-automation-service';
import { ValidationProps } from 'src/components/pages/campaignAutomation/input/assets';
import { ErrorConstants } from 'src/constants';
import { InvalidInputException, ValidationError } from 'src/errors/CustomErrors';
import { logger } from 'src/logger';
import { AssetDetails, AssetState, AssetStatus } from 'src/types';
import { secondsToTime } from 'src/utils/DateTimeUtils';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { formatFileSize } from 'src/utils/formatFileSize';
import { validateImageDimensions } from 'src/utils/validations/Images';
import { validateVideoDimensions, validateVideoLength } from 'src/utils/validations/Videos';

/**
 * Properties for creating an AssetValidationHandler
 *
 * @interface AssetValidationHandlerProps
 * @property {ValidationProps} validationProps - Validation specifications including dimension and video length requirements
 * @property {AssetType} assetType - Type of asset being validated (BACKGROUND_IMAGE, LOGO, VIDEO_TRAILER)
 * @property {Function} setState - State setter function to update asset validation status
 * @property {Function} getTransformationTemplate - Function to retrieve current transformation template
 */
export interface AssetValidationHandlerProps {
  validationProps: ValidationProps;
  assetType: AssetType;
  setState: (assetState: Partial<AssetState>) => void;
  getTransformationTemplate: () => TransformationTemplate;
}
export class AssetValidationHandler {
  private validationProps: ValidationProps;
  private assetType: AssetType;
  private setState: (assetState: Partial<AssetState>) => void;
  private readonly getTransformationTemplate: () => TransformationTemplate;
  private videoDurationData: { videoDuration: number; startTime: string; endTime: string } | null =
    null;

  constructor(props: AssetValidationHandlerProps) {
    if (props.assetType === AssetType.VIDEO_TRAILER && !props.validationProps.videoLengthSpecs) {
      throw new ValidationError('VideoLengthSpecs is undefined');
    }
    this.validationProps = props.validationProps;
    this.assetType = props.assetType;
    this.setState = props.setState;
    this.getTransformationTemplate = props.getTransformationTemplate;
  }

  /**
   * Validates an asset file based on its type and updates the asset state accordingly
   *
   * @param {File} [file] - The file to validate
   * @returns {Promise<void>} A promise that resolves when validation is complete
   */
  async validateAsset(assetDetails: AssetDetails): Promise<void> {
    try {
      if (!assetDetails.file) {
        throw new InvalidInputException('File is undefined');
      }
      this.validateFileSize(assetDetails.file, this.validationProps.maxFileSizeInBytes);
      await this.performAssetTypeValidation(assetDetails.file);
      const transformationTemplate = this.getTransformationTemplate();
      this.setState({
        status:
          transformationTemplate === TransformationTemplate.NO_TRANSFORMATION
            ? AssetStatus.COMPLETED
            : AssetStatus.PENDING_TRANSFORMATION,
        key:
          transformationTemplate === TransformationTemplate.NO_TRANSFORMATION
            ? assetDetails.key
            : undefined,
        ...this.videoDurationData,
      });
    } catch (error) {
      let errorMessage;
      switch (true) {
        case error instanceof ValidationError:
          errorMessage = `${getErrorMessage(error)}. ${ErrorConstants.MESSAGES.TRY_WITH_DIFFERENT_TRANSFORMATION}`;
          break;

        default:
          logger.error('Error in asset validation', { error });
          errorMessage = ErrorConstants.MESSAGES.UNEXPECTED_VALIDATION_ERROR;
          break;
      }

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMessage,
        key: undefined,
      });
    }
  }

  /**
   * Performs validation based on the asset type (BACKGROUND_IMAGE, LOGO, VIDEO_TRAILER)
   *
   * @param {File} file - The file to validate
   * @throws {InvalidInputException} When an unsupported asset type is provided
   * @throws {ValidationError} When validation failure occurs
   * @returns {Promise<void>} A promise that resolves when validation is complete
   */
  private async performAssetTypeValidation(file: File): Promise<void> {
    switch (this.assetType) {
      case AssetType.BACKGROUND_IMAGE:
        await Promise.allSettled([
          validateImageDimensions(file, this.validationProps.dimensionSpecification),
          /* Todo: Uncomment this once the CMS based context is made available to decide if this validation is needed
          JIRA: https://issues.labcollab.net/browse/EDSDAP-10039
          */
          // validateBaselineJpeg(fileObject),
        ]).then((results) => {
          const firstError = results
            .filter((result) => result.status === 'rejected')
            // Extract error from first failed validation to ensure consistent error display
            .map((result) => (result as PromiseRejectedResult).reason)[0];
          if (firstError) throw firstError;
        });
        break;

      case AssetType.LOGO:
        await validateImageDimensions(file, this.validationProps.dimensionSpecification);
        break;

      case AssetType.VIDEO_TRAILER:
        if (!this.validationProps.videoLengthSpecs) {
          throw new InvalidInputException('VideoLengthSpecs is undefined');
        }
        const videoDuration = await Promise.allSettled([
          validateVideoDimensions(file, this.validationProps.dimensionSpecification),
          validateVideoLength(file, this.validationProps.videoLengthSpecs),
        ]).then((results) => {
          const firstError = results
            .filter((result) => result.status === 'rejected')
            // Extract error from first failed validation to ensure consistent error display
            .map((result) => (result as PromiseRejectedResult).reason)[0];
          if (firstError) throw firstError;

          // Return the video duration from the second validation (validateVideoLength)
          return (results[1] as PromiseFulfilledResult<number>).value;
        });
        // Store video duration data for later use in setState
        this.videoDurationData = {
          videoDuration,
          startTime: '00:00',
          endTime: secondsToTime(videoDuration),
        };
        break;

      default:
        throw new InvalidInputException(
          `Unsupported asset type passed in AssetValidationHandler: ${this.assetType}`,
        );
    }
  }

  /**
   * Validates if file size is within acceptable limits
   *
   * @param {File} file - The file to validate
   * @param {number} maxSizeInBytes - Maximum allowed file size in bytes
   * @throws {ValidationError} When file size exceeds maximum allowed size
   */
  private validateFileSize(file: File, maxSizeInBytes: number) {
    if (file.size > maxSizeInBytes) {
      throw new ValidationError(
        `File size ${formatFileSize(file.size)} exceeds maximum allowed size of ${formatFileSize(maxSizeInBytes)}`,
      );
    }
  }
}
